import {BrowserRouter, Route, Routes, Navigate} from 'react-router-dom';
import HeaderComponent from './header/HeaderComponent';
import LogoutComponent from './logout/LogoutComponent';
import LoginComponent from '../../LoginComponent';
import ForgotPasswordComponent from './forgotPassword/ForgotPasswordComponent';
import SignupFormComponent from './signUp/SignupFormComponent';
import ErrorComponent from './error/ErrorComponent';
import AboutTeamComponent from './about/AboutTeamComponent';
import AboutCareersComponent from './about/AboutCareersComponent';
import ServicesWebDevelopmentComponent from './services/ServicesWebDevelopmentComponent';
import ServicesMobileDevelopmentComponent from './services/ServicesMobileDevelopmentComponent';
import ServicesSEOComponent from './services/ServicesSEOComponent';
import ContactComponent from './contact/ContactComponent';
import ContactUsComponent from './settings/ContactUsComponent';
import InboxComponent from './email/InboxComponent';
import SentComponent from './email/SentComponent';
import ViewsComponent from './connections/ViewsComponent';
import WinksComponent from './connections/WinksComponent';
import FavoritesComponent from './connections/FavoritesComponent';
import FavoritesMeComponent from './connections/FavoritesMeComponent';
import ProfileViewComponent from './profile/ProfileViewComponent';
import ProfileUpdateComponent from './profile/ProfileUpdateComponent';
import ProfileManagePhotosComponent from './profile/ProfileManagePhotosComponent';
import ProfilePhotoUploadComponent from './profile/ProfilePhotoUploadComponent';
import AccountSettingsComponent from './settings/AccountSettingsComponent';
import UpgradeMembershipComponent from './settings/UpgradeMembershipComponent';
import BlockedMembersComponent from './settings/BlockedMembersComponent';
import PrivacyPolicyComponent from './settings/PrivacyPolicyComponent';
import TermsOfUseComponent from './settings/TermsOfUseComponent';
import RemoveAccountComponent from './settings/RemoveAccountComponent';
import UpdatePasswordComponent from './settings/UpdatePasswordComponent';
import GeneralHelpComponent from './help/GeneralHelpComponent';
import DatingTipsComponent from './help/DatingTipsComponent';
import FirstDateComponent from './help/FirstDateComponent';
import UpgradedMembershipComponent from './help/UpgradedMembershipComponent';
import AuthProvider, {useAuth} from './security/AuthContext';
import SearchPage from './search/SearchPage';
import SignupStep2Component from './signUp/SignupStep2Component';
import FooterComponent from './footer/FooterComponent';

import 'bootstrap/dist/css/bootstrap.min.css';

function AuthenticatedRoute({children}) {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Show loading while checking auth - prevents component mounting
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Checking authentication...</p>
      </div>
    );
  }
  
  // Only redirect after loading is complete
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children; // Only mount when confirmed authenticated
}

export default function KdlApp() {
  return (
    <div className="KdlApp">
      <AuthProvider>
        <BrowserRouter>
          <HeaderComponent />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginComponent />} />
            <Route path="/signup" element={<SignupFormComponent />} />
            <Route path="/forgotPassword" element={<ForgotPasswordComponent />} />
            <Route path="/about/team" element={<AboutTeamComponent />} />
            <Route path="/about/careers" element={<AboutCareersComponent />} />
            <Route path="/services/web-development" element={<ServicesWebDevelopmentComponent />} />
            <Route path="/services/mobile-development" element={<ServicesMobileDevelopmentComponent />} />
            <Route path="/services/seo" element={<ServicesSEOComponent />} />
            <Route path="/contact" element={<ContactComponent />} />

            {/* Protected Routes */}
            <Route path="/" element={
              <AuthenticatedRoute>
                  <InboxComponent />
              </AuthenticatedRoute>
            } />
            
            <Route path="/home" element={
              <AuthenticatedRoute>
                   <InboxComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/welcome/:username" element={
              <AuthenticatedRoute>
                   <InboxComponent />
              </AuthenticatedRoute>
            } />

           <Route path="/signup/step2" element={
             <AuthenticatedRoute>
             <SignupStep2Component />
             </AuthenticatedRoute>
            } />

            <Route path="/logout" element={
              <AuthenticatedRoute>
                <LogoutComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/email/inbox" element={
              <AuthenticatedRoute>
                <InboxComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/email/sent" element={
              <AuthenticatedRoute>
                <SentComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/connections/views" element={
              <AuthenticatedRoute>
                <ViewsComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/connections/winks" element={
              <AuthenticatedRoute>
                <WinksComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/connections/favorites" element={
              <AuthenticatedRoute>
                <FavoritesComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/connections/favorited-me" element={
              <AuthenticatedRoute>
                <FavoritesMeComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/profile/my-profile" element={
              <AuthenticatedRoute>
                <ProfileViewComponent isOwnProfile={true} />
              </AuthenticatedRoute>
            } />

            <Route path="/profile/view/:passMemberTypeId/:passMemberIdViewed" element={
              <AuthenticatedRoute>
                <ProfileViewComponent isOwnProfile={false} />
              </AuthenticatedRoute>
            } />

            <Route path="/profile/edit-profile" element={
              <AuthenticatedRoute>
                <ProfileUpdateComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/profile/manage-photos" element={
              <AuthenticatedRoute>
                <ProfileManagePhotosComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/profile/add-photos" element={
              <AuthenticatedRoute>
                <ProfilePhotoUploadComponent />
              </AuthenticatedRoute>
            } />

           <Route path="/search" element={
             <AuthenticatedRoute>
               <SearchPage />
             </AuthenticatedRoute>
           } />

            <Route path="/settings/account-settings" element={
              <AuthenticatedRoute>
                <AccountSettingsComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/settings/upgrade-membership" element={
              <AuthenticatedRoute>
                <UpgradeMembershipComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/settings/blocked-members" element={
              <AuthenticatedRoute>
                <BlockedMembersComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/settings/privacy-policy" element={<PrivacyPolicyComponent />} />
            <Route path="/settings/terms-of-use" element={<TermsOfUseComponent />} />

            <Route path="/settings/contact-us" element={
              <AuthenticatedRoute>
               <ContactUsComponent />
             </AuthenticatedRoute>
            } />

            <Route path="/settings/remove-account" element={
              <AuthenticatedRoute>
                <RemoveAccountComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/settings/update-password" element={
              <AuthenticatedRoute>
                <UpdatePasswordComponent />
              </AuthenticatedRoute>
            } />

            <Route path="/help/general" element={<GeneralHelpComponent />} />
            <Route path="/help/dating-tips" element={<DatingTipsComponent />} />
            <Route path="/help/first-date" element={<FirstDateComponent />} />
            <Route path="/help/upgraded-membership" element={<UpgradedMembershipComponent />} />

            {/* Error Route */}
            <Route path="*" element={<ErrorComponent />} />
          </Routes>
          <FooterComponent />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}